/**
 * PERSON FORM BODY — Campi del form contatto, riusabili.
 *
 * Usato da:
 *   - PersonForm (drawer standalone create/edit dalla lista Contatti)
 *   - AddRelationDrawer tab "Crea nuova" (crea + collega)
 *
 * Non inserisce SidePanel ne' i bottoni: il wrapping (header, close,
 * scroll, footer con submit/cancel) e' a carico del consumer. Qui solo
 * lo stato form e i fields. Il `<form id={formId}>` permette al parent
 * di piazzare bottoni submit fuori dal form (footer del SidePanel) con
 * attribute form={formId}.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { searchPersonSourcesAsLookup } from '../personSource/personSourceLookup';
import { searchPersonChannelsAsLookup } from '../personChannel/personChannelLookup';
import { DualFieldAutocomplete } from '../../components/DualFieldAutocomplete';
import { personApi } from './personApi';
import type { Person } from './personMappers';
import { MARKETING_STATUSES, LEAD_STATUSES, type PersonCreatePayload, type MarketingStatus, type LeadStatus } from './personTypes';

interface PersonFormBodyProps {
  /** Id del form HTML: serve ai bottoni submit esterni (footer SidePanel). */
  formId: string;
  /**
   * Classe sul `<form>`: il consumer decide il layout dei campi. PersonForm
   * passa `pf-grid` (due colonne, nel modale largo); AddRelationDrawer non
   * passa nulla e resta a colonna singola, com'e' giusto in un drawer stretto.
   */
  className?: string;
  /** Valori iniziali; in modalita' edit vengono caricati dopo fetch. */
  initialValues?: PersonCreatePayload;
  /** Handler submit: riceve il payload; parent decide se create o update + cleanup. */
  onSubmit: (payload: PersonCreatePayload) => Promise<void>;
  /**
   * Id del contatto in modifica, se presente: escluso dal check anti-duplicato
   * email (un contatto non e' duplicato di se stesso). Assente in creazione.
   */
  currentPersonId?: number;
  /**
   * Notifica il consumer quando il form è bloccato dal check di univocità email
   * (email già in uso da un altro contatto). Il consumer, che possiede il
   * bottone submit nel proprio footer, lo disabilita di conseguenza.
   */
  onBlockingChange?: (blocking: boolean) => void;
}

const DEFAULT_VALUES: PersonCreatePayload = {
  externalCode: '',
  firstName: '', lastName: '', location: '',
  personSourceId: 0, personSource: '',
  personChannelId: 0, personChannel: '',
  leadStatus: 'nuovo', marketingStatus: 'lead',
  email: '', phone: '',
};

export function PersonFormBody({ formId, className, initialValues, onSubmit, currentPersonId, onBlockingChange }: PersonFormBodyProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const groupId = useCurrentGroupId();
  const [form, setForm] = useState<PersonCreatePayload>(initialValues ?? DEFAULT_VALUES);
  // Regola "almeno uno tra nome e cognome": vincolo cross-field non esprimibile
  // come `required` sul singolo campo, quindi validato qui al submit.
  const [nameError, setNameError] = useState(false);
  // Check anti-duplicato email (TS-37): al blur del campo email cerchiamo un
  // contatto del sito con quella mail; se esiste (e non e' il contatto in
  // modifica) apriamo un dialog bloccante che invita a usare l'esistente.
  const [duplicate, setDuplicate] = useState<Person | null>(null);
  // Visibilità del dialog, separata da `duplicate`: il dialog è chiudibile per
  // lasciar correggere l'email, ma il blocco del submit resta finché `duplicate`
  // non viene azzerato (cambio email o nuovo blur senza duplicati).
  const [dialogOpen, setDialogOpen] = useState(false);
  // Ref all'input email: su "Cambia email" ridiamo il focus al campo per far
  // correggere subito l'indirizzo.
  const emailInputRef = useRef<HTMLInputElement>(null);
  // Email per cui l'utente ha gia' scelto "Cambia email": va ignorata dal check
  // al blur, altrimenti la chiusura del dialog (che fa riperdere il focus al
  // campo) lo riaprirebbe subito, senza dare modo di correggere l'indirizzo.
  const dismissedEmailRef = useRef<string | null>(null);

  // Se initialValues cambia (es. dopo fetch in edit mode), risincronizza e
  // azzera l'eventuale errore di validazione legato al record precedente.
  useEffect(() => {
    if (initialValues) {
      setForm(initialValues);
      setNameError(false);
    }
  }, [initialValues]);

  // Il check di univocità email è bloccante: propaga al consumer lo stato
  // "bloccato" così può disabilitare il submit (che vive nel suo footer).
  useEffect(() => {
    onBlockingChange?.(duplicate !== null);
  }, [duplicate, onBlockingChange]);

  // Lookup anti-duplicato per l'email corrente: ritorna il contatto esistente
  // (diverso da quello in modifica) o null. Solo email plausibile (contiene @).
  // Best-effort: su errore ritorna null. Condiviso da blur e submit.
  async function checkDuplicate(): Promise<Person | null> {
    const emailAddress = form.email?.trim();
    if (!emailAddress || !emailAddress.includes('@')) return null;
    try {
      const existing = await personApi.getByEmailAddress(groupId, emailAddress);
      return existing && existing.personId !== currentPersonId ? existing : null;
    } catch {
      return null;
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // Almeno uno tra nome e cognome deve essere valorizzato.
    if (form.firstName.trim().length === 0 && form.lastName.trim().length === 0) {
      setNameError(true);
      return;
    }
    setNameError(false);
    // Univocità email bloccante: riverifica sincrona al submit per chiudere la
    // race blur↔submit (cliccando "Salva" il blur potrebbe non aver ancora
    // risposto). Se duplicato, blocca e mostra il dialog invece di procedere.
    const found = await checkDuplicate();
    setDuplicate(found);
    if (found) {
      setDialogOpen(true);
      return;
    }
    await onSubmit(form);
  }

  async function handleEmailBlur() {
    const emailAddress = form.email?.trim();
    // Solo email plausibile (contiene @): evita lookup su input parziale. Il
    // lookup e' best-effort: se fallisce non blocca la creazione.
    if (!emailAddress || !emailAddress.includes('@')) return;
    // Email gia' scartata via "Cambia email": non riaprire il dialog finche'
    // l'utente non modifica davvero l'indirizzo.
    if (emailAddress === dismissedEmailRef.current) return;
    try {
      const existing = await personApi.getByEmailAddress(groupId, emailAddress);
      if (existing && existing.personId !== currentPersonId) {
        setDuplicate(existing);
        setDialogOpen(true);
      }
    } catch {
      // lookup fallito: nessun avviso, l'utente puo' procedere
    }
  }

  // "Cambia email": chiude il dialog e riporta l'utente sul campo email, ma
  // lascia il blocco attivo (`duplicate` resta) finche' l'email non viene
  // corretta davvero (l'onChange del campo azzera duplicate+dialogOpen). Memorizza
  // l'email scartata (cosi' il blur di chiusura non riapre il dialog) e differisce
  // il focus, perche' finche' il Dialog e' montato il FocusTrap lo catturerebbe.
  function handleChangeEmail() {
    dismissedEmailRef.current = form.email?.trim() ?? null;
    setDialogOpen(false);
    setTimeout(() => emailInputRef.current?.focus(), 0);
  }

  // Fetcher per `Fonte`: closure groupId-bound sopra il helper condiviso.
  // useCallback necessario per identity stabile (useAsyncAutocomplete dentro
  // DualFieldAutocomplete refetcha-rebbe ad ogni render senza).
  const fetchPersonSourceOptions = useCallback(
    (query: string) => searchPersonSourcesAsLookup(groupId, query),
    [groupId],
  );

  const fetchPersonChannelOptions = useCallback(
    (query: string) => searchPersonChannelsAsLookup(groupId, query),
    [groupId],
  );

  return (
    // Ordine dei campi: prima chi identifica il contatto (nome, recapiti), poi
    // provenienza e stati, infine i due campi accessori. In due colonne
    // (`pf-grid`, modale) le coppie cadono sulla stessa riga; a colonna singola
    // (drawer) resta la stessa sequenza, letta dall'alto.
    <form id={formId} className={className} onSubmit={handleSubmit}>
      <TextField label={t('person.firstName')} value={form.firstName} onChange={(e) => { setForm({ ...form, firstName: e.target.value }); if (nameError) setNameError(false); }} error={nameError} fullWidth className="form-field" />
      <TextField label={t('person.lastName')} value={form.lastName} onChange={(e) => { setForm({ ...form, lastName: e.target.value }); if (nameError) setNameError(false); }} error={nameError} helperText={nameError ? t('person.validation.nameRequired') : undefined} fullWidth className="form-field" />
      {/* Email e telefono: campo singolo (il contatto PRIMARIO). La semantica di
          persistenza è decisa dal consumer in handleSubmit — CREATE: nuovo
          contatto primario; EDIT (PersonForm): update del primario esistente, o
          add se assente. */}
      <TextField type="email" label={t('person.emailPrimary')} value={form.email ?? ''} onChange={(e) => { setForm({ ...form, email: e.target.value }); if (duplicate) { setDuplicate(null); setDialogOpen(false); } }} onBlur={handleEmailBlur} inputRef={emailInputRef} error={duplicate !== null} helperText={duplicate ? t('person.duplicateEmail.fieldError') : undefined} fullWidth className="form-field" />
      <TextField type="tel" label={t('person.phonePrimary')} value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} fullWidth className="form-field" />
      <div className="form-field">
        <DualFieldAutocomplete
          value={{ id: form.personSourceId, name: form.personSource }}
          onChange={({ id, name }) =>
            setForm({ ...form, personSourceId: id, personSource: name })
          }
          fetcher={fetchPersonSourceOptions}
          label={t('person.source')}
          placeholder={t('person.personSourcePlaceholder')}
          fullWidth
        />
      </div>
      <div className="form-field">
        <DualFieldAutocomplete
          value={{ id: form.personChannelId, name: form.personChannel }}
          onChange={({ id, name }) =>
            setForm({ ...form, personChannelId: id, personChannel: name })
          }
          fetcher={fetchPersonChannelOptions}
          label={t('person.channel')}
          placeholder={t('person.personChannelPlaceholder')}
          fullWidth
        />
      </div>
      <TextField select label={t('person.marketingStatus')} value={form.marketingStatus} onChange={(e) => setForm({ ...form, marketingStatus: e.target.value as MarketingStatus })} fullWidth className="form-field">
        {MARKETING_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{t(s.label)}</MenuItem>)}
      </TextField>
      <TextField select label={t('person.leadStatus')} value={form.leadStatus} onChange={(e) => setForm({ ...form, leadStatus: e.target.value as LeadStatus })} fullWidth className="form-field">
        {LEAD_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{t(s.label)}</MenuItem>)}
      </TextField>
      <TextField label={t('person.location')} value={form.location ?? ''} onChange={(e) => setForm({ ...form, location: e.target.value })} fullWidth className="form-field" />
      {/* Il codice esterno lo scrive il gestionale, non l'utente: si mostra, se c'è, e non si cambia. */}
      {form.externalCode && (
        <TextField label={t('person.externalCode')} value={form.externalCode} disabled fullWidth className="form-field" />
      )}

      {/* Dialog anti-duplicato email: bloccante (al blur, se l'email e' gia' di
          un altro contatto del sito). I Button MUI sono type="button" di
          default, quindi non fanno submit del form che li contiene. */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} disableRestoreFocus>
        <DialogTitle>{t('person.duplicateEmail.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Trans
              i18nKey="person.duplicateEmail.message"
              values={{
                email: form.email?.trim() ?? '',
                name: duplicate
                  ? `${duplicate.firstName} ${duplicate.lastName}`.trim() ||
                    (form.email?.trim() ?? '')
                  : '',
              }}
              components={{ mail: <strong />, contact: <strong /> }}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleChangeEmail}>
            {t('person.duplicateEmail.changeEmail')}
          </Button>
          <Button
            variant="contained"
            onClick={() => { if (duplicate) navigate(`/contacts/${duplicate.personId}`); }}
          >
            {t('person.duplicateEmail.openExisting')}
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
}
